import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowUp, 
  faArrowDown, 
  faPencilAlt, 
  faTrash,
  faSave
} from '@fortawesome/free-solid-svg-icons';

/**
 * Side action panel for setlist edit mode
 * 
 * @param {array} selectedSongIds - Array of selected song IDs
 * @param {array} setlistSongs - Array of songs in the setlist
 * @param {boolean} hasChanges - Whether there are unsaved changes
 * @param {boolean} isSaving - Whether the setlist is currently saving
 * @param {function} onMoveUp - Function to handle move up action
 * @param {function} onMoveDown - Function to handle move down action
 * @param {function} onEditRole - Function to handle edit role action
 * @param {function} onDelete - Function to handle delete action
 * @param {function} onSave - Function to handle save action
 * @returns {JSX.Element} Side action panel component
 */
const SideActionPanel = ({ 
  selectedSongIds, 
  setlistSongs,
  hasChanges = false,
  isSaving = false,
  onMoveUp,
  onMoveDown,
  onEditRole,
  onDelete,
  onSave
}) => {

  // Can move up if selected song is not at the top
  const canMoveUp = () => {
    if (selectedSongIds.length === 0) return false;
    const selectedId = selectedSongIds[0];
    const songIndex = setlistSongs.findIndex(s => s.song_id === selectedId);
    return songIndex > 0;
  };

  // Can move down if selected song is not at the bottom
  const canMoveDown = () => {
    if (selectedSongIds.length === 0) return false;
    const selectedId = selectedSongIds[0];
    const songIndex = setlistSongs.findIndex(s => s.song_id === selectedId);
    return songIndex < setlistSongs.length - 1 && songIndex >= 0;
  };

  // Get selected song name for display
  const getSelectedSongName = () => {
    if (selectedSongIds.length === 0) return '';
    const selectedSong = setlistSongs.find(s => s.song_id === selectedSongIds[0]);
    return selectedSong ? selectedSong.title : '';
  };

  return (
    <div className={`side-action-panel ${selectedSongIds.length > 0 || hasChanges ? 'active' : ''}`}>
      <div className="panel-content">
        {/* Save Changes button - visible when there are changes */}
        {hasChanges && (
          <div className="save-changes-container">
            <button 
              className={`action-btn save-btn ${isSaving ? 'disabled' : 'active'}`}
              onClick={onSave}
              disabled={isSaving}
              aria-label="Save changes"
            >
              <FontAwesomeIcon icon={faSave} />
              <span className="save-text">{isSaving ? 'Shranjevanje...' : 'Shrani spremembe'}</span>
            </button>
          </div>
        )}
        
        {selectedSongIds.length === 1 ? (
          // Single song selected - show all options
          <>
            <div className="selected-song-info">
              <span className="song-name">{getSelectedSongName()}</span>
            </div>

            <div className="action-buttons move-buttons">
              <button
                className={`action-btn move-up ${canMoveUp() ? 'active' : 'disabled'}`}
                onClick={() => canMoveUp() && onMoveUp(selectedSongIds[0])}
                disabled={!canMoveUp()}
                aria-label="Move up"
              >
                <FontAwesomeIcon icon={faArrowUp} />
              </button>

              <button
                className={`action-btn move-down ${canMoveDown() ? 'active' : 'disabled'}`}
                onClick={() => canMoveDown() && onMoveDown(selectedSongIds[0])}
                disabled={!canMoveDown()}
                aria-label="Move down"
              >
                <FontAwesomeIcon icon={faArrowDown} />
              </button>
            </div>

            <div className="action-buttons">
              <button
                className="action-btn edit-btn"
                onClick={() => {
                  const selectedSong = setlistSongs.find(s => s.song_id === selectedSongIds[0]);
                  if (selectedSong) {
                    onEditRole(selectedSong);
                  }
                }}
                aria-label="Edit role"
              >
                <FontAwesomeIcon icon={faPencilAlt} />
              </button>

              <button
                className="action-btn delete-btn"
                onClick={() => onDelete(selectedSongIds)}
                aria-label="Delete"
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          </>
        ) : !hasChanges ? (
          <div className="no-selection">
            <span>Select a song</span>
          </div>
        ) : null}
      </div>
      
      <style jsx="true">{`
        .save-changes-container {
          margin-bottom: 12px;
          width: 100%;
        }
        
        .save-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          padding: 8px;
          border-radius: 4px;
          background-color: #0d6efd; /* Bootstrap primary color */
          color: white;
          border: none;
          transition: background-color 0.2s;
        }
        
        .save-btn:hover {
          background-color: #0b5ed7;
        }
        
        .save-btn.disabled {
          background-color: #8bb9fe;
          cursor: not-allowed;
        }
        
        .save-text {
          margin-left: 8px;
          font-size: 0.9rem;
        }
      `}</style>
    </div>
  );
};

export default SideActionPanel;